import React, { useState, useEffect } from 'react';
import { Alert, Button, Table, Form, Row, Col, Badge } from '@/components/ui';
import { Vehicle, VehicleProfile, SummerConfiguration, WinterConfiguration, WarrantyStatus, OwnershipStatus } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import vehicleProfileService from '../../../services/vehicleProfileService';

import { areasService, ServiceArea } from '../../../services/areasService';
import VehicleProfilePreview from './VehicleProfilePreview';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { usePermissions } from '../../../hooks/usePermissions';
import { normalizeDateToDateOnly } from '../../../utils/dateUtils';

interface VehicleAboutSectionProps {
  vehicle: Vehicle;
  onUpdate: (vehicle: Vehicle) => void;
  canEdit: boolean;
  isCreateMode?: boolean;
  onSave?: (data: Partial<Vehicle>) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const VehicleAboutSection: React.FC<VehicleAboutSectionProps> = ({ 
  vehicle, 
  onUpdate, 
  canEdit, 
  isCreateMode = false,
  onSave,
  onCancel,
  isSubmitting = false
}) => {
  const { hasPermission } = usePermissions();
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [editedVehicle, setEditedVehicle] = useState<Partial<Vehicle>>(vehicle);
  // Save failures render inline rather than through window.alert, which blocks the page and
  // leaves nothing on screen once dismissed.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<VehicleProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(vehicle.vehicleProfileId || null);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [selectedServiceAreaIds, setSelectedServiceAreaIds] = useState<number[]>(
    vehicle.vehicleServiceAreas?.map(sa => sa.serviceAreaId) || []
  );
  const canManageProfiles = hasPermission('vehicle:profile:manage') || hasPermission('vehicle:edit');

  useEffect(() => {
    if (canManageProfiles) {
      fetchProfiles();
    }
  }, [canManageProfiles]);

  useEffect(() => {
    if (isEditing) {
      fetchServiceAreas();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditedVehicle(vehicle);
    setSelectedProfileId(vehicle.vehicleProfileId || null);
    setSelectedServiceAreaIds(vehicle.vehicleServiceAreas?.map(sa => sa.serviceAreaId) || []);
  }, [vehicle]);

  const fetchProfiles = async () => {
    try {
      const data = await vehicleProfileService.getAllProfiles();
      setProfiles(data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    }
  };

  const fetchServiceAreas = async () => {
    try {
      const data = await areasService.getServiceAreas();
      setServiceAreas(data);
    } catch (error) {
      console.error('Error fetching service areas:', error);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    if (isCreateMode && onSave) {
      // In create mode `editedVehicle` is a display-only stand-in built by VehicleCreatePage,
      // so it carries fields the server must assign itself. `id` is the dangerous one: it is a
      // placeholder 0, and the server passes the request body straight to Prisma, so sending it
      // collides with the existing id-0 row and every create fails on a unique constraint.
      // Strip the server-owned and relation fields; send only the editable columns.
      const {
        id: _id,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        vehicleProfile: _vehicleProfile,
        vehicleServiceAreas: _vehicleServiceAreas,
        ...editableFields
      } = editedVehicle as Record<string, unknown>;
      const createData: any = { ...editableFields };
      createData.vehicleProfileId = selectedProfileId;
      createData.serviceAreaIds = selectedServiceAreaIds;
      
      // Normalize date fields to date-only format (YYYY-MM-DD)
      if (createData.nextCVIP) {
        createData.nextCVIP = normalizeDateToDateOnly(createData.nextCVIP) || createData.nextCVIP;
      }
      
      // Call onSave with the prepared data
      await onSave(createData);
      return;
    }

    try {
      // Include profile ID in update if it changed
      const updateData: any = { ...editedVehicle };
      if (selectedProfileId !== vehicle.vehicleProfileId) {
        // Send null explicitly to clear the profile, or the profile ID to set it
        updateData.vehicleProfileId = selectedProfileId;
      }
      
      // Normalize date fields to date-only format (YYYY-MM-DD)
      if (updateData.nextCVIP) {
        updateData.nextCVIP = normalizeDateToDateOnly(updateData.nextCVIP) || updateData.nextCVIP;
      }
      
      // Include service area IDs
      const currentServiceAreaIds = vehicle.vehicleServiceAreas?.map(sa => sa.serviceAreaId) || [];
      const serviceAreaIdsChanged = JSON.stringify(currentServiceAreaIds.sort()) !== JSON.stringify(selectedServiceAreaIds.sort());
      if (serviceAreaIdsChanged) {
        updateData.serviceAreaIds = selectedServiceAreaIds;
      }
      
      const updated = await vehicleService.updateVehicle(vehicle.id, updateData);
      onUpdate(updated);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to update vehicle.');
    }
  };

  const handleCancel = () => {
    if (isCreateMode && onCancel) {
      onCancel();
      return;
    }
    setEditedVehicle(vehicle);
    setSelectedProfileId(vehicle.vehicleProfileId || null);
    setSelectedServiceAreaIds(vehicle.vehicleServiceAreas?.map(sa => sa.serviceAreaId) || []);
    setSaveError(null);
    setIsEditing(false);
  };

  const handleProfileChange = (profileId: number | null) => {
    setSelectedProfileId(profileId);
  };

  return (
    <div>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h2>About</h2>
        {canEdit && !isEditing && (
          <Button variant="primary" onClick={() => setIsEditing(true)}>
            <i className="bi bi-pencil tw-mr-1"></i>Edit
          </Button>
        )}
        {isEditing && (
          <div>
            <Button
              variant="success"
              className="tw-mr-2"
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isCreateMode ? 'Create' : 'Save'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {saveError && (
        <Alert variant="danger" role="alert" className="tw-mb-3">
          {saveError}
        </Alert>
      )}

      {vehicle.imageUrl && (
        <div className="tw-mb-4">
          <img src={vehicle.imageUrl} alt={vehicle.unitNumber} className="tw-max-w-full tw-h-auto" style={{ maxHeight: '300px' }} />
        </div>
      )}

      <Row>
        <Col md={6}>
          <h4>Basic Information</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Unit Number</th>
                <td>
                  {isEditing ? (
                    <Form.Control
                      value={editedVehicle.unitNumber || ''}
                      onChange={(e) => setEditedVehicle({ ...editedVehicle, unitNumber: e.target.value })}
                    />
                  ) : (
                    vehicle.unitNumber
                  )}
                </td>
              </tr>
              <tr>
                <th>Timberline Unit Number</th>
                <td>
                  {isEditing ? (
                    <Form.Control
                      value={editedVehicle.timberlineUnitNumber || ''}
                      onChange={(e) => setEditedVehicle({ ...editedVehicle, timberlineUnitNumber: e.target.value })}
                    />
                  ) : (
                    vehicle.timberlineUnitNumber || '-'
                  )}
                </td>
              </tr>
              <tr>
                <th>Description</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.description || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, description: e.target.value })}
                  />
                ) : (
                  vehicle.description || '-'
                )}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>
                  {isEditing ? (
                    <Form.Select
                      value={editedVehicle.status || 'ACTIVE'}
                      onChange={(e) => setEditedVehicle({ ...editedVehicle, status: e.target.value })}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </Form.Select>
                  ) : (
                    <Badge bg={vehicle.status === 'ACTIVE' ? 'success' : 'secondary'}>
                      {vehicle.status}
                    </Badge>
                  )}
                </td>
              </tr>
              <tr>
                <th>Year</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    value={editedVehicle.year || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, year: parseInt(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.year || '-'
                )}</td>
              </tr>
              <tr>
                <th>VIN Number</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.vinNumber || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, vinNumber: e.target.value })}
                  />
                ) : (
                  vehicle.vinNumber || '-'
                )}</td>
              </tr>
              <tr>
                <th>Make</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.make || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, make: e.target.value })}
                  />
                ) : (
                  vehicle.make || '-'
                )}</td>
              </tr>
              <tr>
                <th>Model</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.model || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, model: e.target.value })}
                  />
                ) : (
                  vehicle.model || '-'
                )}</td>
              </tr>
            </tbody>
          </Table>

          <h4 className="tw-mt-4">Vehicle Specifications</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Number of Axles</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    value={editedVehicle.numberOfAxles || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, numberOfAxles: parseInt(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.numberOfAxles || '-'
                )}</td>
              </tr>
              <tr>
                <th>Rate</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.rate || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, rate: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.rate ? `$${vehicle.rate.toFixed(2)}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>Vehicle Type</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.vehicleType || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, vehicleType: e.target.value })}
                  />
                ) : (
                  vehicle.vehicleType || '-'
                )}</td>
              </tr>
              <tr>
                <th>Vehicle Classification</th>
                <td>{vehicle.vehicleClassification || '-'}</td>
              </tr>
              <tr>
                <th>Trailer or Attachment</th>
                <td>{vehicle.trailerOrAttachment || '-'}</td>
              </tr>
            </tbody>
          </Table>

          <h4 className="tw-mt-4">Service & Status</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Assigned Service Areas</th>
                <td>
                  {isEditing ? (
                    <MultiSelectDropdown<ServiceArea>
                      options={serviceAreas}
                      value={selectedServiceAreaIds}
                      onChange={(ids) => setSelectedServiceAreaIds(ids as number[])}
                      formatOption={(area) => area.name}
                      getValue={(area) => area.id}
                      placeholder="Select service areas"
                      className="tw-w-full"
                    />
                  ) : (
                    vehicle.vehicleServiceAreas && vehicle.vehicleServiceAreas.length > 0
                      ? vehicle.vehicleServiceAreas.map(sa => sa.serviceArea.name).join(', ')
                      : '-'
                  )}
                </td>
              </tr>
              <tr>
                <th>Current Status</th>
                <td>
                  {isEditing ? (
                    <Form.Control
                      value={editedVehicle.currentStatus || ''}
                      onChange={(e) => setEditedVehicle({ ...editedVehicle, currentStatus: e.target.value })}
                    />
                  ) : (
                    <Badge bg={vehicle.currentStatus === 'In Service' ? 'success' : 'secondary'}>
                      {vehicle.currentStatus || '-'}
                    </Badge>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>

        <Col md={6}>
          <h4>Brake Information</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Brake Type - Front</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.brakeTypeFront || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, brakeTypeFront: e.target.value })}
                  />
                ) : (
                  vehicle.brakeTypeFront || '-'
                )}</td>
              </tr>
              <tr>
                <th>Brake Type - Rear</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.brakeTypeRear || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, brakeTypeRear: e.target.value })}
                  />
                ) : (
                  vehicle.brakeTypeRear || '-'
                )}</td>
              </tr>
              <tr>
                <th>Requires Brake Inspection</th>
                <td>
                  {vehicle.vehicleProfile?.requiresBrakeInspection ? 'Yes' : 'No'}
                </td>
              </tr>
            </tbody>
          </Table>

          <h4 className="tw-mt-4">Technical Information</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Fuel Type</th>
                <td>{vehicle.fuelType || '-'}</td>
              </tr>
              <tr>
                <th>Has GPS</th>
                <td>{vehicle.hasGPS ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>Can Patrol</th>
                <td>{vehicle.canPatrol ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>Comments</th>
                <td>{isEditing ? (
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editedVehicle.comments || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, comments: e.target.value })}
                  />
                ) : (
                  vehicle.comments || '-'
                )}</td>
              </tr>
              <tr>
                <th>Current Odometer</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    value={editedVehicle.currentOdometer || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, currentOdometer: parseInt(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.currentOdometer || '-'
                )}</td>
              </tr>
              <tr>
                <th>Next PM Due</th>
                <td>{vehicle.nextPMDue || '-'}</td>
              </tr>
              <tr>
                <th>Next CVIP</th>
                <td>
                  {isEditing && canEdit ? (
                    <Form.Control
                      type="date"
                      value={editedVehicle.nextCVIP ? normalizeDateToDateOnly(editedVehicle.nextCVIP) || '' : ''}
                      onChange={(e) => setEditedVehicle({ ...editedVehicle, nextCVIP: e.target.value || undefined })}
                    />
                  ) : (
                    vehicle.nextCVIP ? new Date(vehicle.nextCVIP).toLocaleDateString() : '-'
                  )}
                </td>
              </tr>
              <tr>
                <th>Vehicle Profile</th>
                <td>
                  {isEditing && canManageProfiles ? (
                    <div className="tw-flex tw-items-center">
                      <Form.Select
                        value={selectedProfileId || ''}
                        onChange={(e) => handleProfileChange(e.target.value ? parseInt(e.target.value) : null)}
                        style={{ maxWidth: '300px' }}
                      >
                        <option value="">No Profile</option>
                        {profiles.map(profile => (
                          <option key={profile.id} value={profile.id}>{profile.inspection}</option>
                        ))}
                      </Form.Select>
                      {selectedProfileId && (
                        <VehicleProfilePreview 
                          profile={profiles.find(p => p.id === selectedProfileId) || null} 
                        />
                      )}
                    </div>
                  ) : (
                    <div className="tw-flex tw-items-center">
                      <span>{vehicle.vehicleProfile?.inspection || 'No Profile'}</span>
                      {vehicle.vehicleProfile && (
                        <VehicleProfilePreview profile={vehicle.vehicleProfile} />
                      )}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>

          <h4 className="tw-mt-4">Dimensions</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Height</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.height || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, height: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.height ? `${vehicle.height}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>Length</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.length || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, length: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.length ? `${vehicle.length}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>Width</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.width || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, width: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.width ? `${vehicle.width}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>Calculated Center of Gravity</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.calculatedCenterOfGravity || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, calculatedCenterOfGravity: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.calculatedCenterOfGravity ? `${vehicle.calculatedCenterOfGravity}` : '-'
                )}</td>
              </tr>
            </tbody>
          </Table>

          <h4 className="tw-mt-4">Configuration & Ownership</h4>
          <Table bordered>
            <tbody>
              <tr>
                <th>Summer Configuration</th>
                <td>{isEditing ? (
                  <Form.Select
                    value={editedVehicle.summerConfiguration || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, summerConfiguration: e.target.value as SummerConfiguration || undefined })}
                  >
                    <option value="">-</option>
                    <option value={SummerConfiguration.PlowWithSolidSpreader}>Plow with solid spreader</option>
                    <option value={SummerConfiguration.PlowWithBrine}>Plow with brine</option>
                    <option value={SummerConfiguration.WaterTruck}>Water truck</option>
                    <option value={SummerConfiguration.SignTruckWithMobileCrane}>Sign truck with mobile crane</option>
                    <option value={SummerConfiguration.BridgeTruckWithMobileCrane}>Bridge truck with mobile crane</option>
                  </Form.Select>
                ) : (
                  vehicle.summerConfiguration ? vehicle.summerConfiguration.replace(/([A-Z])/g, ' $1').trim() : '-'
                )}</td>
              </tr>
              <tr>
                <th>Winter Configuration</th>
                <td>{isEditing ? (
                  <Form.Select
                    value={editedVehicle.winterConfiguration || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, winterConfiguration: e.target.value as WinterConfiguration || undefined })}
                  >
                    <option value="">-</option>
                    <option value={WinterConfiguration.Tractor}>Tractor</option>
                  </Form.Select>
                ) : (
                  vehicle.winterConfiguration || '-'
                )}</td>
              </tr>
              <tr>
                <th>Spreader System Type</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.spreaderSystemType || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, spreaderSystemType: e.target.value })}
                  />
                ) : (
                  vehicle.spreaderSystemType || '-'
                )}</td>
              </tr>
              <tr>
                <th>Warranty Status</th>
                <td>{isEditing ? (
                  <Form.Select
                    value={editedVehicle.warrantyStatus || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, warrantyStatus: e.target.value as WarrantyStatus || undefined })}
                  >
                    <option value="">-</option>
                    <option value={WarrantyStatus.Active}>Active</option>
                    <option value={WarrantyStatus.Expired}>Expired</option>
                  </Form.Select>
                ) : (
                  vehicle.warrantyStatus || '-'
                )}</td>
              </tr>
              <tr>
                <th>Registration Number</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.registrationNumber || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, registrationNumber: e.target.value })}
                  />
                ) : (
                  vehicle.registrationNumber || '-'
                )}</td>
              </tr>
              <tr>
                <th>Tare Weight</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.tareWeight || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, tareWeight: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.tareWeight ? `${vehicle.tareWeight}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>Wheelbase</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.wheelbase || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, wheelbase: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.wheelbase ? `${vehicle.wheelbase}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>GVWR</th>
                <td>{isEditing ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editedVehicle.gvwr || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, gvwr: parseFloat(e.target.value) || undefined })}
                  />
                ) : (
                  vehicle.gvwr ? `${vehicle.gvwr}` : '-'
                )}</td>
              </tr>
              <tr>
                <th>Current Location</th>
                <td>{isEditing ? (
                  <Form.Control
                    value={editedVehicle.currentLocation || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, currentLocation: e.target.value })}
                  />
                ) : (
                  vehicle.currentLocation || '-'
                )}</td>
              </tr>
              <tr>
                <th>Ownership Status</th>
                <td>{isEditing ? (
                  <Form.Select
                    value={editedVehicle.ownershipStatus || ''}
                    onChange={(e) => setEditedVehicle({ ...editedVehicle, ownershipStatus: e.target.value as OwnershipStatus || undefined })}
                  >
                    <option value="">-</option>
                    <option value={OwnershipStatus.Owned}>Owned</option>
                    <option value={OwnershipStatus.Leased}>Leased</option>
                    <option value={OwnershipStatus.Rented}>Rented</option>
                  </Form.Select>
                ) : (
                  vehicle.ownershipStatus || '-'
                )}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

export default VehicleAboutSection;

